import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  InputBase,
  Checkbox,
  ListItemText,
  Chip,
  Collapse,
  Badge,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { gql, useQuery } from '@apollo/client';
import LoadingIndicator from '@/components/core/LoadingIndicator';
import { startOfDay, subDays } from 'date-fns';
import { translateToTz } from '@/util/dateTime';
import Dropdown from '@/components/core/Dropdown';
import DatePicker, { Range, createRanges } from '@/components/core/DatePicker';
import { LocalDate, Period } from '@js-joda/core';
import RolePieChart from './RolePieChart';
import CostTrendChart from './CostTrendChart';
import StaffExportButton from './StaffExportButton';
import InTestingComponent from '@/components/InTestingComponent';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ClearIcon from '@material-ui/icons/Clear';

const STAFF_ANALYTICS_CONTRIBUTORS = gql`
  query StaffAnalyticsContributors($start: Date!, $end: Date!) {
    staffAnalyticsContributors(startDate: $start, endDate: $end) {
      physicians {
        id
        name
      }
      nurses {
        id
        name
      }
      technicians {
        id
        name
      }
      anesthesiologists {
        id
        name
      }
    }
  }
`;

const STAFF_ANALYTICS_QUERY = gql`
  query StaffAnalytics(
    $startDate: Date!
    $endDate: Date!
    $aggregation: String
    $physicianIds: [Long!]
    $nurseIds: [Long!]
    $technicianIds: [Long!]
    $anesthesiologistIds: [Long!]
    $departments: [String!]
    $roles: [String!]
  ) {
    staffAnalyticsSummary(
      startDate: $startDate
      endDate: $endDate
      physicianIds: $physicianIds
      nurseIds: $nurseIds
      technicianIds: $technicianIds
      anesthesiologistIds: $anesthesiologistIds
      departments: $departments
      roles: $roles
    ) {
      roleData {
        role
        totalTimeInHours
        totalCost
        locations {
          locationId
          locationName
          locationType
          timeInHours
          cost
        }
      }
      totalTimeInHours
      totalCost
    }
    staffCostAndHoursOverTime(
      startDate: $startDate
      endDate: $endDate
      aggregation: $aggregation
      physicianIds: $physicianIds
      nurseIds: $nurseIds
      technicianIds: $technicianIds
      anesthesiologistIds: $anesthesiologistIds
      departments: $departments
      roles: $roles
    ) {
      name
      anesthesiologistHours
      anesthesiologistCost
      staffHours
      staffCost
      totalHours
      totalCost
    }
  }
`;

const STAFF_ANALYTICS_AVAILABLE_DEPARTMENTS = gql`
  query StaffAnalyticsAvailableDepartments {
    staffAnalyticsAvailableDepartments
  }
`;

type DateRangeType = 'today' | 'last7' | 'last30' | 'custom';
type RoleType = 'PHYSICIAN' | 'NURSE' | 'TECHNICIAN' | 'ANESTHESIOLOGIST';
const ALL_ROLES: RoleType[] = ['PHYSICIAN', 'NURSE', 'TECHNICIAN', 'ANESTHESIOLOGIST'];
type Aggregation = 'hourly' | 'daily' | 'weekly' | 'monthly';
type ChartMode = 'cost' | 'hours';
// Sentinel value sent to backend to represent "department IS NULL / unassigned".
// Backend must interpret this value accordingly (see notes below in query variables).
const NONE_DEPARTMENT_VALUE = '__NONE_DEPARTMENT__';

interface StaffAnalyticsProps {
  hospitalTimezone: string;
  view: 'table' | 'analytics';
  onViewChange: (view: 'table' | 'analytics') => void;
  hospitalName: string;
  initialSelectedDate?: string | null;
}

const getDefaultAggregationForRange = (start: Date, end: Date): Aggregation => {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const inclusiveDays = Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;

  if (inclusiveDays <= 1) return 'hourly';
  if (inclusiveDays <= 7) return 'daily';
  if (inclusiveDays <= 60) return 'weekly';
  return 'monthly';
};

const getRoomColor = (type: string): string => {
  switch (type?.toUpperCase()) {
    case 'OR':
    case 'OPERATING_ROOM':
      return '#74AC48'; // Green
    case 'PRE_OP':
    case 'PRE-OP':
    case 'PREOP':
      return '#FFBB28'; // Yellow
    case 'POST_OP':
    case 'POST-OP':
    case 'POSTOP':
      return '#3D5A80'; // Blue/Gray
    case 'PACU':
      return '#2B3C87'; // Dark Blue
    case 'BREAK_ROOM':
    case 'BREAK':
      return '#3D5A80';
    case 'WAITING_ROOM':
    case 'WAITING':
      return '#00A7F7'; // Cyan
    case 'UNKNOWN':
    case 'VIRTUAL':
      return '#5B9BD5'; // Gray
    default:
      return '#9E9E9E';
  }
};

// Custom Input for Select to match the dark theme
const DarkInput = withStyles(theme => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontSize: 14,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    color: theme.palette.text.primary,
    '&:focus': {
      borderRadius: theme.shape.borderRadius,
      borderColor: '#5B9BD5',
      boxShadow: '0 0 0 0.2rem rgba(91,155,213,0.25)',
    },
  },
}))(InputBase);

const PickerDropdownContent = ({ close, today, value, onChange, ranges, picker, setPicker }: any) => {
  const handleChange = React.useCallback(
    (next: Range, done: boolean) => {
      setPicker(next);
      if (done) {
        close(() => onChange(next));
      }
    },
    [setPicker, close, onChange]
  );

  return (
    <div>
      <DatePicker today={today} value={picker} onChange={handleChange} ranges={ranges} />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    minHeight: '100vh',
    backgroundColor: '#04153A',
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  content: {
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(3),
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  headerArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  titleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  hospitalName: {
    fontSize: '1.75rem',
    fontWeight: 600,
    color: '#FFFFFF',
    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },
  },
  subtitle: {
    fontSize: '0.875rem', // text-sm
    color: 'rgba(255, 255, 255, 0.6)',
    marginTop: theme.spacing(0.5),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tabsContainer: {
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    marginTop: theme.spacing(1),
  },
  tabsNav: {
    display: 'flex',
    gap: theme.spacing(4),
    fontSize: '1.25rem',
  },
  activeTab: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: -1,
    borderBottom: '2px solid #00A3FF',
    color: '#FFFFFF',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    cursor: 'pointer',
    fontSize: '1.25rem',
    transition: 'color 0.2s',
    '&:focus': {
      outline: 'none',
    },
  },
  inactiveTab: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: -1,
    borderBottom: '2px solid transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    cursor: 'pointer',
    fontSize: '1.25rem',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#FFFFFF',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  // Analytics specific controls
  analyticsControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  presetButton: {
    minWidth: 'auto',
    borderRadius: theme.shape.borderRadius,
    lineHeight: 1,
  },
  presetButtonActive: {
    backgroundColor: '#00A3FF !important',
    color: '#FFFFFF !important',
    '&:hover': {
      backgroundColor: '#00A3FF !important',
    },
  },
  dateRangeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  dateDisplayTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
  },
  dateInputLike: {
    backgroundColor: '#020B26',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: '1rem',
    color: '#FFFFFF',
    minWidth: 120,
    textAlign: 'center',
  },
  chartsGrid: {
    display: 'grid',
    width: '100%',
    boxSizing: 'border-box',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: theme.spacing(2),
    height: '100%',
    gridAutoRows: '1fr',
    overflow: 'hidden',
  },
  fullHeight: {
    height: '100%',
    minHeight: 0,
  },
  filterSelect: {
    width: '100%',
    maxWidth: 240,
  },
  menuItem: {
    fontSize: 14,
  },
  menuPaper: {
    backgroundColor: '#020B26',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  menuCheckbox: {
    color: '#00A3FF',
    padding: '4px 8px 4px 0',
  },
  // Global filters container
  filtersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  // Primary filters row (roles + department + clear)
  primaryFiltersRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 500,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Role chips
  roleChipsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  roleChip: {
    backgroundColor: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.8125rem',
    height: 32,
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 163, 255, 0.1)',
      borderColor: 'rgba(0, 163, 255, 0.3)',
    },
    '&:focus': {
      backgroundColor: 'rgba(0, 163, 255, 0.1)',
    },
  },
  roleChipActive: {
    backgroundColor: 'rgba(0, 163, 255, 0.2)',
    borderColor: '#00A3FF',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: 'rgba(0, 163, 255, 0.3)',
    },
  },
  // Department select
  departmentSelect: {
    minWidth: 160,
  },
  // Clear filters button
  clearFiltersButton: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.8125rem',
    textTransform: 'none',
    padding: '4px 12px',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  clearIcon: {
    fontSize: 16,
  },
  // Staff members expandable section
  staffMembersSection: {
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    paddingTop: theme.spacing(1),
  },
  staffMembersHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(0.5, 0),
    userSelect: 'none',
    '&:hover': {
      '& $staffMembersLabel': {
        color: 'rgba(255, 255, 255, 0.9)',
      },
    },
  },
  staffMembersLabel: {
    fontSize: '0.8125rem',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 500,
    transition: 'color 0.15s ease',
  },
  expandIcon: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 20,
  },
  staffFiltersBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: '#00A3FF',
      color: '#FFFFFF',
      fontSize: '0.6875rem',
      minWidth: 18,
      height: 18,
    },
  },
  staffFiltersContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    paddingTop: theme.spacing(1),
  },
  staffFilterSelect: {
    minWidth: 180,
    maxWidth: 240,
  },
  // Divider
  filterDivider: {
    width: 1,
    height: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0 4px',
  },
  // Dropdown footer with Done button
  dropdownFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  dropdownSelectedCount: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  dropdownDoneButton: {
    backgroundColor: '#00A3FF',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    padding: '6px 16px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#0090e0',
    },
  },
}));

const StaffAnalytics: React.FC<StaffAnalyticsProps> = ({
  hospitalTimezone,
  onViewChange,
  hospitalName,
  initialSelectedDate,
  view,
}) => {
  const classes = useStyles();

  const today = useMemo(() => startOfDay(translateToTz(hospitalTimezone)(new Date())), [hospitalTimezone]);
  const localToday = useMemo(() => LocalDate.now(), []);

  const [dateRangeType, setDateRangeType] = useState<DateRangeType>(() => {
    const fmt = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const todayStr = fmt(today);
    if (!initialSelectedDate || initialSelectedDate === todayStr) return 'today';
    return 'custom';
  });
  const [customDateRange, setCustomDateRange] = useState<Range>(() => {
    const fmt = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const todayStr = fmt(today);
    if (!initialSelectedDate || initialSelectedDate === todayStr) {
      return Range.of(LocalDate.now(), Period.ofDays(1), LocalDate.now());
    }
    const ld = LocalDate.parse(initialSelectedDate);
    return Range.of(ld, Period.ofDays(1), ld);
  });
  const [picker, setPicker] = useState<Range>(customDateRange);
  const [aggregation, setAggregation] = useState<Aggregation>(() => {
    const fmt = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const todayStr = fmt(today);
    if (!initialSelectedDate || initialSelectedDate === todayStr) {
      return getDefaultAggregationForRange(today, today);
    }
    const ld = LocalDate.parse(initialSelectedDate);
    const rangeStart = new Date(ld.toString() + 'T00:00:00.000');
    const rangeEnd = new Date(ld.toString() + 'T00:00:00.000');
    return getDefaultAggregationForRange(rangeStart, rangeEnd);
  });
  const [activeIndexes, setActiveIndexes] = useState<Record<string, number | undefined>>({});
  const [selectedPhysicianIds, setSelectedPhysicianIds] = useState<number[]>([]);
  const [selectedNurseIds, setSelectedNurseIds] = useState<number[]>([]);
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState<number[]>([]);
  const [selectedAnesthesiologistIds, setSelectedAnesthesiologistIds] = useState<number[]>([]);
  const [chartMode, setChartMode] = useState<ChartMode>('cost');
  const [selectedRoles, setSelectedRoles] = useState<RoleType[]>(ALL_ROLES);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [staffFiltersExpanded, setStaffFiltersExpanded] = useState<boolean>(false);

  // Pending selections for staff member dropdowns (applied on close)
  const [pendingPhysicianIds, setPendingPhysicianIds] = useState<number[]>([]);
  const [pendingNurseIds, setPendingNurseIds] = useState<number[]>([]);
  const [pendingTechnicianIds, setPendingTechnicianIds] = useState<number[]>([]);
  const [pendingAnesthesiologistIds, setPendingAnesthesiologistIds] = useState<number[]>([]);

  // Open state for staff member dropdowns (controlled)
  const [physicianDropdownOpen, setPhysicianDropdownOpen] = useState(false);
  const [nurseDropdownOpen, setNurseDropdownOpen] = useState(false);
  const [technicianDropdownOpen, setTechnicianDropdownOpen] = useState(false);
  const [anesthesiologistDropdownOpen, setAnesthesiologistDropdownOpen] = useState(false);

  // Handlers for dropdown open/close to sync pending state
  const handlePhysicianDropdownOpen = () => {
    setPendingPhysicianIds(selectedPhysicianIds);
    setPhysicianDropdownOpen(true);
  };
  const handlePhysicianDropdownClose = () => {
    setSelectedPhysicianIds(pendingPhysicianIds);
    setPhysicianDropdownOpen(false);
  };

  const handleNurseDropdownOpen = () => {
    setPendingNurseIds(selectedNurseIds);
    setNurseDropdownOpen(true);
  };
  const handleNurseDropdownClose = () => {
    setSelectedNurseIds(pendingNurseIds);
    setNurseDropdownOpen(false);
  };

  const handleTechnicianDropdownOpen = () => {
    setPendingTechnicianIds(selectedTechnicianIds);
    setTechnicianDropdownOpen(true);
  };
  const handleTechnicianDropdownClose = () => {
    setSelectedTechnicianIds(pendingTechnicianIds);
    setTechnicianDropdownOpen(false);
  };

  const handleAnesthesiologistDropdownOpen = () => {
    setPendingAnesthesiologistIds(selectedAnesthesiologistIds);
    setAnesthesiologistDropdownOpen(true);
  };
  const handleAnesthesiologistDropdownClose = () => {
    setSelectedAnesthesiologistIds(pendingAnesthesiologistIds);
    setAnesthesiologistDropdownOpen(false);
  };

  // Calculate total staff members selected
  const totalStaffSelected = useMemo(
    () =>
      selectedPhysicianIds.length +
      selectedNurseIds.length +
      selectedTechnicianIds.length +
      selectedAnesthesiologistIds.length,
    [selectedPhysicianIds, selectedNurseIds, selectedTechnicianIds, selectedAnesthesiologistIds]
  );

  // Check if any filters are active (not default state)
  const hasActiveFilters = useMemo(
    () => selectedRoles.length < ALL_ROLES.length || selectedDepartments.length > 0 || totalStaffSelected > 0,
    [selectedRoles, selectedDepartments, totalStaffSelected]
  );

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedRoles(ALL_ROLES);
    setSelectedDepartments([]);
    setSelectedPhysicianIds([]);
    setSelectedNurseIds([]);
    setSelectedTechnicianIds([]);
    setSelectedAnesthesiologistIds([]);
  };

  // Toggle a role
  const handleRoleToggle = (role: RoleType) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        // Don't allow deselecting all roles
        if (prev.length === 1) return prev;
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
    // Clear specific staff filters for this role when deselecting
    if (selectedRoles.includes(role)) {
      switch (role) {
        case 'PHYSICIAN':
          setSelectedPhysicianIds([]);
          break;
        case 'NURSE':
          setSelectedNurseIds([]);
          break;
        case 'TECHNICIAN':
          setSelectedTechnicianIds([]);
          break;
        case 'ANESTHESIOLOGIST':
          setSelectedAnesthesiologistIds([]);
          break;
      }
    }
  };

  React.useEffect(() => {
    setPicker(customDateRange);
  }, [customDateRange]);

  const { startDate, endDate } = useMemo(() => {
    switch (dateRangeType) {
      case 'today':
        return { startDate: today, endDate: today };
      case 'last7':
        return { startDate: subDays(today, 6), endDate: today };
      case 'last30':
        return { startDate: subDays(today, 29), endDate: today };
      case 'custom':
        const rangeStart = new Date(customDateRange.firstIncluded().toString() + 'T00:00:00.000');
        const rangeEnd = new Date(customDateRange.lastIncluded().toString() + 'T00:00:00.000');
        return { startDate: rangeStart, endDate: rangeEnd };
      default:
        return { startDate: today, endDate: today };
    }
  }, [dateRangeType, customDateRange, today]);

  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (date: Date): string => {
    // Format as YYYY-MM-DD for input-like display, or simple date
    // Reference used input type="date" value which is YYYY-MM-DD
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isToday = useMemo(() => {
    const todayStr = formatLocalDate(today);
    return formatLocalDate(startDate) === todayStr && formatLocalDate(endDate) === todayStr;
  }, [startDate, endDate, today]);

  const { data: contributorsData, loading: contributorsLoading } = useQuery(STAFF_ANALYTICS_CONTRIBUTORS, {
    variables: {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate),
    },
    pollInterval: isToday ? 60000 : undefined,
  });

  const { loading, error, data } = useQuery(STAFF_ANALYTICS_QUERY, {
    variables: {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate),
      aggregation,
      physicianIds: selectedPhysicianIds.length > 0 ? selectedPhysicianIds : null,
      nurseIds: selectedNurseIds.length > 0 ? selectedNurseIds : null,
      technicianIds: selectedTechnicianIds.length > 0 ? selectedTechnicianIds : null,
      anesthesiologistIds: selectedAnesthesiologistIds.length > 0 ? selectedAnesthesiologistIds : null,
      departments: selectedDepartments.length > 0 ? selectedDepartments : null,
      roles: selectedRoles.length > 0 && selectedRoles.length < ALL_ROLES.length ? selectedRoles : null,
    },
    pollInterval: isToday ? 60000 : undefined,
  });

  const { data: availableDepartmentsData } = useQuery(STAFF_ANALYTICS_AVAILABLE_DEPARTMENTS, {
    pollInterval: isToday ? 60000 : undefined,
  });

  const costAndHoursOverTimeData = useMemo(() => {
    if (!data?.staffCostAndHoursOverTime) return [];

    return data.staffCostAndHoursOverTime.map((point: any) => ({
      time: point.name,
      cost: point.totalCost,
      hours: point.totalHours,
      // Keep raw data for potential detailed tooltips
      anesthesiologistCost: point.anesthesiologistCost,
      anesthesiologistHours: point.anesthesiologistHours,
      staffCost: point.staffCost,
      staffHours: point.staffHours,
    }));
  }, [data]);

  const chartTotal = useMemo(() => {
    if (chartMode === 'cost') {
      return costAndHoursOverTimeData.reduce((sum, point) => sum + point.cost, 0);
    }
    return costAndHoursOverTimeData.reduce((sum, point) => sum + point.hours, 0);
  }, [costAndHoursOverTimeData, chartMode]);

  if (loading && !data) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <pre>Error: {String(error)}</pre>;
  }

  const roleData = data?.staffAnalyticsSummary?.roleData || [];

  return (
    <Box className={classes.container}>
      {/* Header */}
      <div className={classes.headerArea}>
        <div className={classes.titleColumn}>
          <div>
            <Typography variant="h1" className={classes.hospitalName}>
              Staffing Analytics
            </Typography>
          </div>

          {/* Date Presets & Picker */}
          <div className={classes.analyticsControls}>
            <ToggleButtonGroup value={dateRangeType}>
              <ToggleButton
                value="today"
                className={`${classes.presetButton} ${dateRangeType === 'today' ? classes.presetButtonActive : ''}`}
                onClick={() => {
                  setAggregation('hourly');
                  setDateRangeType('today');
                }}
              >
                Today
              </ToggleButton>
              <ToggleButton
                value="last7"
                className={`${classes.presetButton} ${dateRangeType === 'last7' ? classes.presetButtonActive : ''}`}
                onClick={() => {
                  setAggregation('daily');
                  setDateRangeType('last7');
                }}
              >
                Last 7 days
              </ToggleButton>
              <ToggleButton
                value="last30"
                className={`${classes.presetButton} ${dateRangeType === 'last30' ? classes.presetButtonActive : ''}`}
                onClick={() => {
                  setAggregation('weekly');
                  setDateRangeType('last30');
                }}
              >
                Last 30 days
              </ToggleButton>
            </ToggleButtonGroup>

            <div className={classes.dateRangeDisplay}>
              <span>Date range:</span>
              <Dropdown
                contentComponent={PickerDropdownContent}
                contentProps={{
                  today: localToday,
                  value: customDateRange,
                  onChange: (range: Range) => {
                    const rangeStart = new Date(range.firstIncluded().toString() + 'T00:00:00.000');
                    const rangeEnd = new Date(range.lastIncluded().toString() + 'T00:00:00.000');
                    setAggregation(getDefaultAggregationForRange(rangeStart, rangeEnd));
                    setCustomDateRange(range);
                    setDateRangeType('custom');
                  },
                  ranges: createRanges({ today: localToday }),
                  picker,
                  setPicker,
                }}
              >
                <div className={classes.dateDisplayTrigger}>
                  <div className={classes.dateInputLike}>{formatDateDisplay(startDate)}</div>
                  <span>to</span>
                  <div className={classes.dateInputLike}>{formatDateDisplay(endDate)}</div>
                </div>
              </Dropdown>
            </div>

            <InTestingComponent>
              <StaffExportButton startDate={formatLocalDate(startDate)} endDate={formatLocalDate(endDate)} />
            </InTestingComponent>
          </div>
        </div>

        {/* Tabs row */}
        <div className={classes.tabsContainer}>
          <div className={classes.tabsNav}>
            <button
              className={view === 'table' ? classes.activeTab : classes.inactiveTab}
              onClick={() => onViewChange('table')}
            >
              Table View
            </button>
            <button
              className={view === 'analytics' ? classes.activeTab : classes.inactiveTab}
              onClick={() => onViewChange('analytics')}
            >
              Analytics
            </button>
          </div>
        </div>

        {/* Global Filters */}
        <InTestingComponent>
          <div className={classes.filtersContainer}>
            {/* Primary Filters Row: Role Chips + Department + Clear */}
            <div className={classes.primaryFiltersRow}>
              {/* Role Chips */}
              <div className={classes.filterGroup}>
                <span className={classes.filterLabel}>Roles</span>
                <div className={classes.roleChipsContainer}>
                  {ALL_ROLES.map(role => (
                    <Chip
                      key={role}
                      label={role.charAt(0) + role.slice(1).toLowerCase()}
                      onClick={() => handleRoleToggle(role)}
                      className={`${classes.roleChip} ${selectedRoles.includes(role) ? classes.roleChipActive : ''}`}
                      size="small"
                    />
                  ))}
                </div>
              </div>

              <div className={classes.filterDivider} />

              {/* Department Filter */}
              <div className={classes.filterGroup}>
                <span className={classes.filterLabel}>Department</span>
                <Select
                  multiple
                  value={selectedDepartments}
                  displayEmpty
                  onChange={e => setSelectedDepartments(e.target.value as string[])}
                  input={<DarkInput />}
                  className={classes.departmentSelect}
                  renderValue={(selected: unknown) => {
                    const selectedArray = selected as string[];
                    if (selectedArray.length === 0) {
                      return 'All';
                    }
                    if (selectedArray.length === 1) {
                      return selectedArray[0] === NONE_DEPARTMENT_VALUE ? 'None' : selectedArray[0];
                    }
                    return `${selectedArray.length} selected`;
                  }}
                  MenuProps={{
                    PaperProps: { className: classes.menuPaper },
                  }}
                >
                  <MenuItem key={NONE_DEPARTMENT_VALUE} value={NONE_DEPARTMENT_VALUE} className={classes.menuItem}>
                    <Checkbox
                      checked={selectedDepartments.includes(NONE_DEPARTMENT_VALUE)}
                      size="small"
                      className={classes.menuCheckbox}
                    />
                    <ListItemText primary="None" />
                  </MenuItem>

                  {(availableDepartmentsData?.staffAnalyticsAvailableDepartments || [])
                    .filter((dept: any) => typeof dept === 'string' && dept.trim() !== '')
                    .map((dept: string) => (
                    <MenuItem key={dept} value={dept} className={classes.menuItem}>
                      <Checkbox
                        checked={selectedDepartments.includes(dept)}
                        size="small"
                        className={classes.menuCheckbox}
                      />
                      <ListItemText primary={dept} />
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Clear Filters - only show when filters are active */}
              {hasActiveFilters && (
                <>
                  <div className={classes.filterDivider} />
                  <button className={classes.clearFiltersButton} onClick={handleClearFilters}>
                    <ClearIcon className={classes.clearIcon} />
                    Clear filters
                  </button>
                </>
              )}
            </div>

            {/* Staff Members Expandable Section */}
            <div className={classes.staffMembersSection}>
              <div
                className={classes.staffMembersHeader}
                onClick={() => setStaffFiltersExpanded(!staffFiltersExpanded)}
              >
                <Badge
                  badgeContent={totalStaffSelected}
                  className={classes.staffFiltersBadge}
                  invisible={totalStaffSelected === 0}
                >
                  <span className={classes.staffMembersLabel}>Filter by specific staff members</span>
                </Badge>
                {staffFiltersExpanded ? (
                  <ExpandLessIcon className={classes.expandIcon} />
                ) : (
                  <ExpandMoreIcon className={classes.expandIcon} />
                )}
              </div>

              <Collapse in={staffFiltersExpanded}>
                <div className={classes.staffFiltersContent}>
                  {/* Physician Filter */}
                  {selectedRoles.includes('PHYSICIAN') && (
                    <div className={classes.filterGroup}>
                      <Select
                        multiple
                        open={physicianDropdownOpen}
                        value={pendingPhysicianIds}
                        displayEmpty
                        onOpen={handlePhysicianDropdownOpen}
                        onClose={handlePhysicianDropdownClose}
                        onChange={e => setPendingPhysicianIds((e.target.value as number[]).filter(id => id != null))}
                        input={<DarkInput />}
                        className={classes.staffFilterSelect}
                        renderValue={() => {
                          if (selectedPhysicianIds.length === 0) {
                            return 'All Physicians';
                          }
                          if (selectedPhysicianIds.length === 1) {
                            const name = (contributorsData?.staffAnalyticsContributors?.physicians || []).find(
                              (opt: any) => opt.id === selectedPhysicianIds[0]
                            )?.name;
                            return name || '1 selected';
                          }
                          return `${selectedPhysicianIds.length} Physicians`;
                        }}
                        MenuProps={{
                          PaperProps: { className: classes.menuPaper },
                          MenuListProps: { style: { paddingBottom: 0 } },
                        }}
                      >
                        {(contributorsData?.staffAnalyticsContributors?.physicians || []).map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id} className={classes.menuItem}>
                            <Checkbox
                              checked={pendingPhysicianIds.includes(opt.id)}
                              size="small"
                              className={classes.menuCheckbox}
                            />
                            <ListItemText primary={opt.name} />
                          </MenuItem>
                        ))}
                        <div className={classes.dropdownFooter} onMouseDown={e => e.preventDefault()}>
                          <span className={classes.dropdownSelectedCount}>{pendingPhysicianIds.length} selected</span>
                          <button className={classes.dropdownDoneButton} onClick={handlePhysicianDropdownClose}>
                            Done
                          </button>
                        </div>
                      </Select>
                    </div>
                  )}

                  {/* Nurse Filter */}
                  {selectedRoles.includes('NURSE') && (
                    <div className={classes.filterGroup}>
                      <Select
                        multiple
                        open={nurseDropdownOpen}
                        value={pendingNurseIds}
                        displayEmpty
                        onOpen={handleNurseDropdownOpen}
                        onClose={handleNurseDropdownClose}
                        onChange={e => setPendingNurseIds((e.target.value as number[]).filter(id => id != null))}
                        input={<DarkInput />}
                        className={classes.staffFilterSelect}
                        renderValue={() => {
                          if (selectedNurseIds.length === 0) {
                            return 'All Nurses';
                          }
                          if (selectedNurseIds.length === 1) {
                            const name = (contributorsData?.staffAnalyticsContributors?.nurses || []).find(
                              (opt: any) => opt.id === selectedNurseIds[0]
                            )?.name;
                            return name || '1 selected';
                          }
                          return `${selectedNurseIds.length} Nurses`;
                        }}
                        MenuProps={{
                          PaperProps: { className: classes.menuPaper },
                          MenuListProps: { style: { paddingBottom: 0 } },
                        }}
                      >
                        {(contributorsData?.staffAnalyticsContributors?.nurses || []).map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id} className={classes.menuItem}>
                            <Checkbox
                              checked={pendingNurseIds.includes(opt.id)}
                              size="small"
                              className={classes.menuCheckbox}
                            />
                            <ListItemText primary={opt.name} />
                          </MenuItem>
                        ))}
                        <div className={classes.dropdownFooter} onMouseDown={e => e.preventDefault()}>
                          <span className={classes.dropdownSelectedCount}>{pendingNurseIds.length} selected</span>
                          <button className={classes.dropdownDoneButton} onClick={handleNurseDropdownClose}>
                            Done
                          </button>
                        </div>
                      </Select>
                    </div>
                  )}

                  {/* Technician Filter */}
                  {selectedRoles.includes('TECHNICIAN') && (
                    <div className={classes.filterGroup}>
                      <Select
                        multiple
                        open={technicianDropdownOpen}
                        value={pendingTechnicianIds}
                        displayEmpty
                        onOpen={handleTechnicianDropdownOpen}
                        onClose={handleTechnicianDropdownClose}
                        onChange={e => setPendingTechnicianIds((e.target.value as number[]).filter(id => id != null))}
                        input={<DarkInput />}
                        className={classes.staffFilterSelect}
                        renderValue={() => {
                          if (selectedTechnicianIds.length === 0) {
                            return 'All Technicians';
                          }
                          if (selectedTechnicianIds.length === 1) {
                            const name = (contributorsData?.staffAnalyticsContributors?.technicians || []).find(
                              (opt: any) => opt.id === selectedTechnicianIds[0]
                            )?.name;
                            return name || '1 selected';
                          }
                          return `${selectedTechnicianIds.length} Technicians`;
                        }}
                        MenuProps={{
                          PaperProps: { className: classes.menuPaper },
                          MenuListProps: { style: { paddingBottom: 0 } },
                        }}
                      >
                        {(contributorsData?.staffAnalyticsContributors?.technicians || []).map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id} className={classes.menuItem}>
                            <Checkbox
                              checked={pendingTechnicianIds.includes(opt.id)}
                              size="small"
                              className={classes.menuCheckbox}
                            />
                            <ListItemText primary={opt.name} />
                          </MenuItem>
                        ))}
                        <div className={classes.dropdownFooter} onMouseDown={e => e.preventDefault()}>
                          <span className={classes.dropdownSelectedCount}>{pendingTechnicianIds.length} selected</span>
                          <button className={classes.dropdownDoneButton} onClick={handleTechnicianDropdownClose}>
                            Done
                          </button>
                        </div>
                      </Select>
                    </div>
                  )}

                  {/* Anesthesiologist Filter */}
                  {selectedRoles.includes('ANESTHESIOLOGIST') && (
                    <div className={classes.filterGroup}>
                      <Select
                        multiple
                        open={anesthesiologistDropdownOpen}
                        value={pendingAnesthesiologistIds}
                        displayEmpty
                        onOpen={handleAnesthesiologistDropdownOpen}
                        onClose={handleAnesthesiologistDropdownClose}
                        onChange={e =>
                          setPendingAnesthesiologistIds((e.target.value as number[]).filter(id => id != null))
                        }
                        input={<DarkInput />}
                        className={classes.staffFilterSelect}
                        renderValue={() => {
                          if (selectedAnesthesiologistIds.length === 0) {
                            return 'All Anesthesiologists';
                          }
                          if (selectedAnesthesiologistIds.length === 1) {
                            const name = (contributorsData?.staffAnalyticsContributors?.anesthesiologists || []).find(
                              (opt: any) => opt.id === selectedAnesthesiologistIds[0]
                            )?.name;
                            return name || '1 selected';
                          }
                          return `${selectedAnesthesiologistIds.length} Anesthesiologists`;
                        }}
                        MenuProps={{
                          PaperProps: { className: classes.menuPaper },
                          MenuListProps: { style: { paddingBottom: 0 } },
                        }}
                      >
                        {(contributorsData?.staffAnalyticsContributors?.anesthesiologists || []).map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id} className={classes.menuItem}>
                            <Checkbox
                              checked={pendingAnesthesiologistIds.includes(opt.id)}
                              size="small"
                              className={classes.menuCheckbox}
                            />
                            <ListItemText primary={opt.name} />
                          </MenuItem>
                        ))}
                        <div className={classes.dropdownFooter} onMouseDown={e => e.preventDefault()}>
                          <span className={classes.dropdownSelectedCount}>
                            {pendingAnesthesiologistIds.length} selected
                          </span>
                          <button className={classes.dropdownDoneButton} onClick={handleAnesthesiologistDropdownClose}>
                            Done
                          </button>
                        </div>
                      </Select>
                    </div>
                  )}

                  {/* Show message if no roles selected that support staff filtering */}
                  {selectedRoles.length === 0 && (
                    <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      Select at least one role to filter by staff members
                    </Typography>
                  )}
                </div>
              </Collapse>
            </div>
          </div>
        </InTestingComponent>
      </div>

      <Box className={classes.content}>
        {/* Pie Charts */}
        <Box className={classes.chartsGrid}>
          {roleData.map((role: any) => {
            const locationData = role.locations.map((loc: any) => ({
              name: loc.locationName,
              value: loc.timeInHours,
              cost: loc.cost,
              color: getRoomColor(loc.locationType),
            }));

            return (
              <Box key={role.role} className={classes.fullHeight}>
                <RolePieChart
                  role={role.role}
                  totalTimeInHours={role.totalTimeInHours}
                  locationData={locationData}
                  activeIndex={activeIndexes[role.role]}
                  onMouseEnter={index => setActiveIndexes(prev => ({ ...prev, [role.role]: index }))}
                  onMouseLeave={() => setActiveIndexes(prev => ({ ...prev, [role.role]: undefined }))}
                />
              </Box>
            );
          })}
        </Box>

        {/* Trend Chart */}
        <Box className={classes.fullHeight}>
          <CostTrendChart
            title={chartMode === 'cost' ? 'Total Cost' : 'Total Hours'}
            totalValue={chartTotal}
            costOverTimeData={costAndHoursOverTimeData}
            aggregation={aggregation}
            onAggregationChange={setAggregation}
            chartMode={chartMode}
            onChartModeChange={setChartMode}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default StaffAnalytics;
